import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ShoppingCart, RefreshCw, ChevronRight, Search, FileText } from 'lucide-react';
import { BRANDS } from '../constants/lpg';
import { printChallan } from '../utils/printChallan';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtINR(n) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n || 0);
}

const today    = new Date().toISOString().split('T')[0];
const daysAgo30 = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];

const FILTER_DEFAULT = { brand: '', godown: '', customer: '', from: daysAgo30, to: today, search: '' };

export default function SaleLogs() {
  const { authHeader, company } = useAuth();
  const companyName = company?.name || 'VayuSarthi';
  const [sales,     setSales]     = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filters,   setFilters]   = useState(FILTER_DEFAULT);
  const [loading,   setLoading]   = useState(true);

  const godownNames = company?.godowns || [];

  const loadSales = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.from)   params.from_date = filters.from;
      if (filters.to)     params.to_date   = filters.to;
      if (filters.godown) params.godown    = filters.godown;
      const res = await axios.get(`${API}/sales`, { headers: authHeader(), params });
      setSales(res.data);
    } catch (err) {
      console.error('Sales load error', err);
    } finally {
      setLoading(false);
    }
  }, [filters.from, filters.to, filters.godown]);

  useEffect(() => {
    axios.get(`${API}/customers`, { headers: authHeader() })
      .then(r => setCustomers(r.data))
      .catch(console.error);
  }, []);

  useEffect(() => { loadSales(); }, [loadSales]);

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  // Enrich each sale with computed filled/mt totals from items
  const enriched = sales.map(r => ({
    ...r,
    filled: (r.items || []).reduce((s, i) => s + (i.filled || 0), 0),
    mt:     (r.items || []).reduce((s, i) => s + (i.mt     || 0), 0),
  }));

  // Client-side filters for brand and search
  const filtered = enriched.filter(r => {
    if (filters.brand && !(r.items || []).some(i => i.brand === filters.brand)) return false;
    if (filters.customer && !r.customer_name?.toLowerCase().includes(filters.customer.toLowerCase())) return false;
    if (filters.search && ![r.challan_no, r.customer_name, r.vehicle]
      .join(' ').toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  });

  const totals = {
    filled:  filtered.reduce((s, r) => s + r.filled, 0),
    mt:      filtered.reduce((s, r) => s + r.mt,     0),
    kg:      filtered.reduce((s, r) => s + (r.total_kg     || 0), 0),
    amount:  filtered.reduce((s, r) => s + (r.total_amount || 0), 0),
  };
  const avgRate = totals.kg > 0 ? totals.amount / totals.kg : 0;

  const selectCls = 'px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:border-gray-400 bg-white text-gray-700';

  return (
    <div className="px-6 py-6">
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <div className="small-label text-gray-400 mb-1 flex items-center gap-1.5">
            <ShoppingCart size={11} /> Sale Logs
          </div>
          <h1 className="font-black text-3xl tracking-tight">Sale Logs</h1>
          <p className="text-sm text-gray-500 mt-1">
            Every sale — filter by brand, customer, godown &amp; date
          </p>
        </div>
        <button onClick={loadSales} className="btn-secondary flex items-center gap-1.5 text-sm self-start">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-5">
        <div className="flex flex-wrap gap-3">
          <select className={selectCls} value={filters.brand} onChange={e => set('brand', e.target.value)}>
            <option value="">All brands</option>
            {BRANDS.map(b => <option key={b}>{b}</option>)}
          </select>
          <select className={selectCls} value={filters.godown} onChange={e => set('godown', e.target.value)}>
            <option value="">All godowns</option>
            {godownNames.map(g => <option key={g}>{g}</option>)}
          </select>
          <select className={selectCls} value={filters.customer} onChange={e => set('customer', e.target.value)}>
            <option value="">All customers</option>
            {customers.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <span className="small-label text-gray-400">FROM</span>
            <input type="date" className={selectCls} value={filters.from} onChange={e => set('from', e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <span className="small-label text-gray-400">TO</span>
            <input type="date" className={selectCls} value={filters.to} onChange={e => set('to', e.target.value)} />
          </div>
          <div className="relative flex-1 min-w-[180px]">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              className="pl-8 pr-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:border-gray-400 w-full bg-white"
              placeholder="Challan / customer / vehicle"
              value={filters.search}
              onChange={e => set('search', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-5">
        {[
          { label: 'Total Sales',      value: fmtINR(totals.amount) },
          { label: 'Filled Cylinders', value: totals.filled },
          { label: 'MT Cylinders',     value: totals.mt },
          { label: 'Total KG',         value: totals.kg.toLocaleString('en-IN', { minimumFractionDigits: 2 }) },
          { label: 'Avg ₹ / KG',       value: `₹ ${avgRate.toFixed(2)}` },
        ].map(s => (
          <div key={s.label} className="border border-gray-200 rounded-lg px-4 py-3 bg-white">
            <div className="small-label text-gray-400 mb-1">{s.label}</div>
            <div className="font-bold text-[15px] text-gray-900">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {['Date', 'Challan', 'Customer', 'Godown', 'Filled', 'MT', 'KG', 'Amount ₹', 'Vehicle', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left small-label text-gray-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="text-center py-8 text-gray-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-8 text-gray-400">No sales found for the selected filters</td></tr>
              ) : filtered.map(r => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-[13px] text-gray-500 whitespace-nowrap">{r.date}</td>
                  <td className="px-4 py-3 text-[13px] font-semibold text-gray-800">{r.challan_no}</td>
                  <td className="px-4 py-3">
                    <div className="text-[13px] font-medium text-gray-800">{r.customer_name || '—'}</div>
                    <div className="text-[11px] text-gray-400">{r.customer_phone || ''}</div>
                  </td>
                  <td className="px-4 py-3 text-[13px] text-gray-600 whitespace-nowrap">{r.godown}</td>
                  <td className="px-4 py-3 text-[13px] font-semibold text-green-700">{r.filled}</td>
                  <td className="px-4 py-3 text-[13px] font-semibold text-amber-700">{r.mt || '—'}</td>
                  <td className="px-4 py-3 text-[13px] text-gray-700">{(r.total_kg || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-[13px] font-semibold text-gray-900 whitespace-nowrap">
                    {fmtINR(r.total_amount)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-[13px] text-gray-600">{r.vehicle || '—'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => printChallan(r, companyName)}
                      title="Download Challan PDF"
                      className="flex items-center gap-1 text-[12px] text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap transition-colors"
                      data-testid={`challan-pdf-btn-${r.id}`}
                    >
                      <FileText size={12} /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-gray-100 text-xs text-gray-400">
          {filtered.length} entries
        </div>
      </div>
    </div>
  );
}
